import { z } from 'zod';

/**
 * What the Refer view shows: a partner's referral links and their discount codes.
 *
 * The two are not the same thing and must not be presented as one list. **Links carry
 * UTM tags and are measured by GA4; codes are what actually attributes a sale.** A
 * partner can have a link with no code and a code with no link, and the numbers beside
 * each come from different systems.
 *
 * WHO OWNS A CODE. Shopify holds the discount — its value, window and usage limit — but
 * has no notion of a code belonging to a partner. **That relationship is this
 * application's and lives in this database**, created by an admin and never inferred.
 * Everything downstream depends on it: `syncOrder` attributes a sale by looking the
 * order's code up in this map, and an unmapped code is audited rather than guessed at.
 */

export const referralLinkRowSchema = z.object({
  id: z.string(),
  label: z.string(),
  url: z.string(),
  channel: z.string(),
  placement: z.string().nullable(),
  /**
   * Sessions on this link's CHANNEL, not on this link.
   *
   * GA4 is queried by source and medium, so the finest figure that exists is per
   * channel — and two links sharing a channel therefore show the same number. That is
   * not a rounding artefact, it is what was measured, and the column is labelled
   * accordingly. Splitting it between the links would be a fabrication.
   *
   * Null when analytics has not synced. Null is not zero.
   */
  channelSessions: z.number().int().nullable(),
});
export type ReferralLinkRow = z.infer<typeof referralLinkRowSchema>;

/**
 * Code status, derived from the window and usage rather than stored.
 *
 * Storing it would need a job to flip `active` to `expired` at midnight, and would be
 * wrong for however long that job was late.
 */
export const CODE_STATUSES = ['active', 'scheduled', 'expired', 'used'] as const;
export const codeStatusSchema = z.enum(CODE_STATUSES);
export type CodeStatus = z.infer<typeof codeStatusSchema>;

export const discountCodeRowSchema = z.object({
  id: z.string(),
  code: z.string(),
  amountText: z.string(),
  startsAt: z.string().nullable(),
  endsAt: z.string().nullable(),
  status: codeStatusSchema,
  /** Orders attributed to this code. Counted from the ledger, not from Shopify. */
  used: z.number().int(),
  /** Null means unlimited — rendered as ∞, never as 0. */
  usageLimit: z.number().int().nullable(),
});
export type DiscountCodeRow = z.infer<typeof discountCodeRowSchema>;

export const referResponseSchema = z.object({
  partnerId: z.string(),
  partnerName: z.string(),
  /** The link a partner is most likely to want to hand out. Null before any exists. */
  primaryLink: z.string().nullable(),
  primaryCode: z.string().nullable(),
  links: z.array(referralLinkRowSchema),
  codes: z.array(discountCodeRowSchema),
  /**
   * Sessions across ALL of this partner's links, since the programme opened.
   *
   * Still no PER-LINK figure, and deliberately so: GA4 is queried by source and medium,
   * which several of a partner's links can share, so the finest grain that exists is
   * the channel — see `channelSessions` on each row. Going finer would need
   * `utm_content` as a fourth dimension, which multiplies the report's rows against a
   * shared hourly quota for a number nobody has asked for.
   *
   * Null when analytics has not synced. Null is not zero.
   */
  sessions: z.number().int().nullable(),
  /**
   * Each channel's share, busiest first — the breakdown the link rows draw from.
   *
   * These add up to `sessions` by construction: both are derived from the same stored
   * rows, whose grain is (partner, bucket, medium).
   */
  channels: z.array(
    z.object({
      medium: z.string(),
      sessions: z.number().int(),
    }),
  ),
});
export type ReferResponse = z.infer<typeof referResponseSchema>;

/**
 * A code's status from its window and usage, at a given instant.
 *
 * Exhausted beats expired: "Used 10/10" is the more useful thing to tell a partner
 * whose code stopped working, and the prototype's Used tag says exactly that.
 */
export function codeStatusOf(
  code: { startsAt: Date | null; endsAt: Date | null; usageLimit: number | null },
  used: number,
  now: Date,
): CodeStatus {
  if (code.usageLimit !== null && used >= code.usageLimit) return 'used';
  if (code.startsAt && now < code.startsAt) return 'scheduled';
  if (code.endsAt && now > code.endsAt) return 'expired';
  return 'active';
}

// ---------------------------------------------------------------- registering a code

/**
 * An admin mapping a Shopify discount code to a partner.
 *
 * The code is the only required field. Everything describing the discount is read from
 * Shopify rather than retyped, because two systems holding the same "20% off" that can
 * drift apart is a support ticket waiting to happen.
 */
export const registerCodeInputSchema = z.object({
  /**
   * Stored upper-cased. Shopify treats codes case-insensitively at checkout, so two
   * mappings differing only in case would be two owners for one code.
   */
  code: z
    .string()
    .trim()
    .min(2)
    .max(64)
    .regex(/^[A-Za-z0-9_-]+$/, 'letters, digits, hyphen and underscore only'),
  /**
   * Register a code Shopify does not have yet.
   *
   * Off by default, and the default is the point: a typo produces a mapping that never
   * matches an order, and the only symptom is commission quietly never accruing. Setting
   * this is an admin saying "I am about to create it", and it is recorded in the audit
   * log as exactly that.
   */
  allowMissingInShopify: z.boolean().default(false),
  /**
   * A fallback description, used only when Shopify has none to give.
   *
   * Shopify's own value always wins, so this is not an override — it is what stops a
   * code registered without a Shopify match (or in an environment with no credentials)
   * from reading as a bare "Discount".
   */
  amountText: z.string().trim().min(1).max(120).optional(),
});
export type RegisterCodeInput = z.infer<typeof registerCodeInputSchema>;

export const VERIFICATION_STATES = ['verified', 'not-found', 'unchecked'] as const;
export const verificationSchema = z.enum(VERIFICATION_STATES);
export type Verification = z.infer<typeof verificationSchema>;

export const registerCodeResultSchema = z.object({
  partnerId: z.string(),
  partnerName: z.string(),
  code: discountCodeRowSchema,
  /**
   * What the Shopify check found.
   *
   * `unchecked` means Shopify is not configured in this environment — which must not be
   * confused with `verified`, or a store with no credentials would silently accept every
   * typo.
   */
  verification: verificationSchema,
  /** Shopify's own title for the discount, when it was found. */
  shopifyTitle: z.string().nullable(),
});
export type RegisterCodeResult = z.infer<typeof registerCodeResultSchema>;
